// src/components/MapChartEdit.tsx

import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { Lock } from "lucide-react";
import MapChart from "./MapChart.tsx";

const WORKER_URL = "https://speemap.ryanhardestylewis.workers.dev";

interface MapChartEditProps {}

const MapChartEdit: React.FC<MapChartEditProps> = () => {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [sessionToken, setSessionToken] = useState<string>("");
  const [inputError, setInputError] = useState(false);

  useEffect(() => {
    let token = Cookies.get("speemap_sessionToken");
    if (!token) {
      token = uuidv4();
      Cookies.set("speemap_sessionToken", token, { expires: 1 });
    }
    setSessionToken(token || "");
    checkToken(token || "");
  }, []);

  async function checkToken(token: string) {
    try {
      const response = await fetch(`${WORKER_URL}/check-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionToken: token }),
      });
      if (!response.ok) {
        console.warn("check-user request failed:", response.status);
        setHasAccess(false);
        return;
      }
      setHasAccess(true);
    } catch (error) {
      console.error("Error checking token:", error);
      setHasAccess(false);
    }
  }

  async function handleSubmitPassword() {
    try {
      const response = await fetch(`${WORKER_URL}/editcheck`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: passwordInput,
          sessionToken,
        }),
      });

      if (response.ok) {
        setHasAccess(true);
      } else {
        const errBody = await response.json().catch(() => null);
        console.error("Password submit failed:", errBody || "Unknown error");
        setInputError(true);
        setTimeout(() => setInputError(false), 2000); // revert after 2s
      }
    } catch (error) {
      console.error("Error contacting server:", error);
    }
  }

  return (
    <div className="relative w-full h-screen">
      {!hasAccess && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-md shadow-lg p-6 w-80 text-center">
            <div className="flex flex-col items-center mb-4">
              <Lock className="text-gray-700 w-10 h-10 mb-2" />
              <h2 className="text-xl font-bold">Enter Password to Edit</h2>
            </div>
            <input
              type="password"
              className={`border rounded px-3 py-2 w-full mb-4 ${inputError ? "border-red-500" : "border-gray-300"}`}
              placeholder="Password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
            />
            <button
              onClick={handleSubmitPassword}
              className="bg-blue-600 text-white px-4 py-2 rounded font-semibold"
            >
              Submit
            </button>
          </div>
        </div>
      )}

      <div
        className={`w-full h-full transition-filter duration-300 ${
          hasAccess ? "blur-0" : "blur-sm pointer-events-none"
        }`}
      >
        {/* Pass sessionToken & hasAccess to MapChart */}
        <MapChart hasAccess={hasAccess} sessionToken={sessionToken} />
      </div>
    </div>
  );
};

export default MapChartEdit;
